import React, { useState, useEffect } from 'react';

export default function ReconcileCredits() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user is admin from token
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role === 'admin') {
        setIsAdmin(true);
        fetchReport();
      } else {
        window.location.href = '/';
      }
    } catch (e) {
      window.location.href = '/login';
    } finally {
      setCheckingAuth(false);
    }
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/reconcile/credits', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setReport(data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fixMissingCredit = async (orderId, pickupId, amount) => {
    if (!window.confirm(`Fix missing credit for order ${orderId.slice(-8)}?`)) return;
    
    setFixing(orderId);
    try {
      const res = await fetch('/api/admin/reconcile/fix-missing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          orderId,
          pickupId,
          amount,
          reason: 'Admin reconciliation'
        })
      });
      const data = await res.json();
      alert('Credit added successfully');
      fetchReport();
    } catch (error) {
      alert('Failed to add credit');
    } finally {
      setFixing(null);
    }
  };

  if (checkingAuth) return <div className="text-center py-8">Checking authentication...</div>;
  if (!isAdmin) return null;
  if (loading) return <div className="text-center py-8">Loading reconciliation report...</div>;
  if (!report) return <div className="text-center py-8">Failed to load report</div>;

const fixAllMissing = async () => {
  if (!window.confirm(`Fix all ${report.missing.length} missing credits?`)) return;
  
  setFixing('all');
  try {
    const res = await fetch('/api/admin/reconcile/fix-all-missing', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ missing: report.missing })
    });
    const data = await res.json();
    alert(`Fixed ${data.fixed} credits`);
    fetchReport();
  } catch (error) {
    alert('Failed to fix all');
  

} finally {
    setFixing(null);
  }
};

  const { summary, missing, matched } = report;

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Credit Reconciliation</h1>

<button
  onClick={fixAllMissing}
  disabled={fixing === 'all' || report.missing.length === 0}
  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
>
  {fixing === 'all' ? 'Fixing...' : `Fix All Missing (${report.missing.length})`}
</button>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Delivered Orders</div>
          <div className="text-2xl font-bold">{summary.totalDeliveredOrders}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Credit Transactions</div>
          <div className="text-2xl font-bold">{summary.totalCreditTransactions}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Matched</div>
          <div className="text-2xl font-bold text-green-600">{summary.matchedCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Missing Credits</div>
          <div className="text-2xl font-bold text-red-600">{summary.missingCount}</div>
        </div>
      </div>

      {/* Missing Credits Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
        <h2 className="text-xl font-semibold p-4 border-b">Missing Credits</h2>
        {missing.length === 0 ? (
          <div className="text-center py-8 text-green-600">All orders have credits! ✓</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">Order #</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Pickup</th>
                  <th className="px-4 py-3 text-right">Order Total</th>
                  <th className="px-4 py-3 text-right">Est. Credit (5%)</th>
                  <th className="px-4 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {missing.map(order => {
                  const estimatedCredit = Math.round(order.totalAmount * 0.05);
                  return (
                    <tr key={order.orderId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono">#{order.orderId.slice(-8)}</td>
                      <td className="px-4 py-3">{new Date(order.orderDate).toLocaleDateString()}</td>
                      <td className="px-4 py-3">{order.pickup}</td>
                      <td className="px-4 py-3 text-right">${order.totalAmount.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{estimatedCredit}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => fixMissingCredit(order.orderId, order.pickupId, estimatedCredit)}
                          disabled={fixing === order.orderId}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:bg-gray-400"
                        >
                          {fixing === order.orderId ? 'Fixing...' : 'Fix Credit'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Matched Credits Table */}
      <details className="bg-white rounded-lg shadow">
        <summary className="text-lg font-semibold p-4 cursor-pointer hover:bg-gray-50">
          Matched Credits ({matched.length})
        </summary>
        <div className="overflow-x-auto border-t">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Order #</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Pickup</th>
                <th className="px-4 py-3 text-right">Credit</th>
                <th className="px-4 py-3 text-center">Has Issuer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {matched.map(order => (
                <tr key={order.orderId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono">#{order.orderId.slice(-8)}</td>
                  <td className="px-4 py-3">{new Date(order.orderDate).toLocaleDateString()}</td>
                  <td className="px-4 py-3">{order.pickup}</td>
                  <td className="px-4 py-3 text-right font-semibold text-green-600">{order.creditAmount}</td>
                  <td className="px-4 py-3 text-center">
                    {order.hasIssuer ? '✓' : '❌'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
